/**
 * Admin Dashboard — summary stats landing page for the notification platform.
 */
import { useQuery } from '@tanstack/react-query';
import {
  BellRing,
  CheckCircle2,
  Clock,
  FileText,
  Loader2,
  Mail,
  MessageSquare,
  RefreshCw,
  SendHorizonal,
  UserCog,
  XCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { dashboardApi, type DashboardStats } from './api';

function StatCard({
  icon: Icon,
  label,
  value,
  sub,
  color,
  iconBg,
}: {
  icon: typeof Mail;
  label: string;
  value: number | string;
  sub?: string;
  color: string;
  iconBg: string;
}) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm hover:shadow-md transition-all duration-300 group">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[12px] font-medium text-slate-500 uppercase tracking-wider mb-1">
            {label}
          </p>
          <p className={`text-3xl font-bold ${color} tabular-nums`}>{value}</p>
          {sub && (
            <p className="text-[11px] text-slate-400 mt-1">{sub}</p>
          )}
        </div>
        <div className={`w-10 h-10 rounded-xl ${iconBg} flex items-center justify-center transition-transform duration-300 group-hover:scale-110`}>
          <Icon size={18} className="text-white" />
        </div>
      </div>
    </div>
  );
}

function ChannelBar({
  label,
  value,
  total,
  color,
  icon: Icon,
}: {
  label: string;
  value: number;
  total: number;
  color: string;
  icon: typeof Mail;
}) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div className="flex items-center gap-4">
      <div className="flex items-center gap-2 w-28 shrink-0">
        <Icon size={14} className="text-slate-400" />
        <span className="text-[13px] font-medium text-slate-600">{label}</span>
      </div>
      <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full ${color} transition-all duration-700 ease-out`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-[13px] font-semibold text-slate-700 tabular-nums w-14 text-right">
        {value}
      </span>
      <span className="text-[11px] text-slate-400 w-10 text-right">{pct}%</span>
    </div>
  );
}

export function DashboardPage() {
  const statsQuery = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: () => dashboardApi.stats(),
    refetchInterval: 60_000,
  });

  const stats = statsQuery.data;

  if (statsQuery.isLoading && !statsQuery.isPlaceholderData) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 size={24} className="animate-spin text-[#008ED3]" />
        <span className="ml-2 text-[13px] text-slate-500">Loading dashboard…</span>
      </div>
    );
  }

  if (statsQuery.error || !stats) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3 text-center">
        <div className="bg-red-50 p-3 rounded-full">
          <XCircle size={24} className="text-red-500" />
        </div>
        <div>
          <p className="text-sm font-semibold text-gray-900">Failed to load dashboard</p>
          <p className="text-[13px] text-slate-500 mt-0.5">
            {statsQuery.error instanceof Error ? statsQuery.error.message : 'An unexpected error occurred'}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => statsQuery.refetch()}
          className="mt-2"
        >
          <RefreshCw size={14} className="mr-2" />
          Try Again
        </Button>
      </div>
    );
  }

  const d = stats?.delivery24h ?? {
    total: 0,
    sent: 0,
    failed: 0,
    queued: 0,
    skipped: 0,
    emailTotal: 0,
    whatsappTotal: 0,
    inAppTotal: 0,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <BellRing size={20} className="text-[#008ED3]" />
            Dashboard
          </h2>
          <p className="text-[13px] text-slate-500 mt-1">
            Notification platform overview — last 24 hours
          </p>
        </div>
        <Button
          variant="ghost_blue"
          size="sm"
          onClick={() => statsQuery.refetch()}
          disabled={statsQuery.isFetching}
          className="gap-1.5"
        >
          <RefreshCw size={13} className={statsQuery.isFetching ? 'animate-spin' : ''} />
          Refresh
        </Button>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={SendHorizonal}
          label="Total Sent"
          value={d.sent}
          sub="Last 24 hours"
          color="text-gray-900"
          iconBg="bg-[#008ED3]"
        />
        <StatCard
          icon={XCircle}
          label="Failed"
          value={d.failed}
          sub="Delivery failures"
          color={d.failed > 0 ? 'text-red-600' : 'text-gray-900'}
          iconBg={d.failed > 0 ? 'bg-red-500' : 'bg-slate-400'}
        />
        <StatCard
          icon={UserCog}
          label="Sender Profiles"
          value={stats?.activeProfiles ?? 0}
          sub="Active configurations"
          color="text-gray-900"
          iconBg="bg-violet-500"
        />
        <StatCard
          icon={FileText}
          label="Templates"
          value={stats?.activeTemplates ?? 0}
          sub="Active templates"
          color="text-gray-900"
          iconBg="bg-amber-500"
        />
      </div>

      {/* Delivery breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Channel breakdown */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <h3 className="text-[14px] font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Clock size={15} className="text-slate-400" />
            Delivery by Channel (24h)
          </h3>
          <div className="space-y-3">
            <ChannelBar
              label="Email"
              value={d.emailTotal}
              total={d.total}
              color="bg-[#008ED3]"
              icon={Mail}
            />
            <ChannelBar
              label="WhatsApp"
              value={d.whatsappTotal}
              total={d.total}
              color="bg-emerald-500"
              icon={MessageSquare}
            />
            <ChannelBar
              label="In-App"
              value={d.inAppTotal}
              total={d.total}
              color="bg-violet-500"
              icon={BellRing}
            />
          </div>
          <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between">
            <span className="text-[12px] text-slate-400">Total notifications</span>
            <span className="text-[14px] font-bold text-gray-900 tabular-nums">{d.total}</span>
          </div>
        </div>

        {/* Status breakdown */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <h3 className="text-[14px] font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <CheckCircle2 size={15} className="text-slate-400" />
            Delivery Status (24h)
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-100">
              <span className="text-[11px] text-emerald-600 font-medium uppercase tracking-wider">Sent</span>
              <p className="text-xl font-bold text-emerald-700 mt-0.5 tabular-nums">{d.sent}</p>
            </div>
            <div className="p-3 rounded-lg bg-red-50 border border-red-100">
              <span className="text-[11px] text-red-600 font-medium uppercase tracking-wider">Failed</span>
              <p className="text-xl font-bold text-red-700 mt-0.5 tabular-nums">{d.failed}</p>
            </div>
            <div className="p-3 rounded-lg bg-amber-50 border border-amber-100">
              <span className="text-[11px] text-amber-600 font-medium uppercase tracking-wider">Queued</span>
              <p className="text-xl font-bold text-amber-700 mt-0.5 tabular-nums">{d.queued}</p>
            </div>
            <div className="p-3 rounded-lg bg-slate-50 border border-slate-100">
              <span className="text-[11px] text-slate-600 font-medium uppercase tracking-wider">Skipped</span>
              <p className="text-xl font-bold text-slate-700 mt-0.5 tabular-nums">{d.skipped}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
